// Panneau de sélection de dossier : recherche debouncée + récents.
// Extrait de DossierSwitcher pour être partagé avec le filtre de la vue Tâches.
// Ne porte NI positionnement NI navigation : chaque appelant fournit sa coquille
// (Popover côté Tâches, absolute côté fiche dossier) et son action onPick.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FolderOpen, Search } from 'lucide-react';
import { useDossiers, type Dossier } from '../../hooks/queries/useDossiers';
import { useDossierQuickAccess } from '../../hooks/useDossierQuickAccess';
import { TypeBadge, StageBadge } from './dossierBadges';

export interface DossierPickerPanelProps {
  dossiers: Dossier[];
  excludeId?: string | null;
  header?: React.ReactNode;
  onPick: (id: string) => void;
}

export function DossierPickerPanel({
  dossiers,
  excludeId = null,
  header,
  onPick,
}: DossierPickerPanelProps) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const { recentIds } = useDossierQuickAccess();

  // Debounce 200ms (même pattern que DossierSearchPalette).
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 200);
    return () => clearTimeout(t);
  }, [query]);

  // Résultats de recherche (uniquement quand on tape).
  const { data: searchData, isFetching } = useDossiers({
    search: debounced.trim() || undefined,
  });

  const byId = useMemo(() => {
    const m = new Map<string, Dossier>();
    dossiers.forEach((d) => m.set(d.id, d));
    return m;
  }, [dossiers]);

  const resolve = useCallback(
    (ids: string[]): Dossier[] =>
      ids
        .filter((id) => id !== excludeId)
        .map((id) => byId.get(id))
        .filter((d): d is Dossier => !!d),
    [byId, excludeId],
  );

  const recents = useMemo(
    () => resolve(recentIds),
    [recentIds, resolve],
  );
  const searchResults = useMemo(
    () => (searchData ?? []).filter((d) => d.id !== excludeId).slice(0, 10),
    [searchData, excludeId],
  );

  // Liste complète (hors recherche), triée alphabétiquement — le conteneur
  // scrolle, pas besoin de tronquer.
  const all = useMemo(
    () =>
      dossiers
        .filter((d) => d.id !== excludeId)
        .sort((a, b) => a.title.localeCompare(b.title, 'fr')),
    [dossiers, excludeId],
  );

  const searching = debounced.trim().length > 0;

  return (
    <>
      {/* Recherche */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-neutral-200">
        <Search className="w-4 h-4 text-neutral-400 shrink-0" />
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher un dossier…"
          className="flex-1 text-sm outline-none placeholder:text-neutral-400"
        />
      </div>

      <div className="max-h-[70vh] overflow-y-auto py-1">
        {header}
        {searching ? (
          <SearchResults results={searchResults} isFetching={isFetching} onPick={onPick} />
        ) : (
          <DefaultLists recents={recents} all={all} onPick={onPick} />
        )}
      </div>
    </>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-neutral-400">
      {children}
    </p>
  );
}

function DossierRow({
  dossier,
  icon,
  onPick,
}: {
  dossier: Dossier;
  icon: React.ReactNode;
  onPick: (id: string) => void;
}) {
  return (
    <li role="option">
      <button
        type="button"
        onClick={() => onPick(dossier.id)}
        className="flex items-center gap-2.5 w-full px-3 py-2 text-left hover:bg-neutral-50 transition-colors"
      >
        <span className="shrink-0">{icon}</span>
        <div className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-cime-text-primary truncate">
            {dossier.title}
          </span>
          <span className="block text-[11px] text-cime-text-muted">
            {dossier.number}
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <TypeBadge type={dossier.type_affaire} className="text-[10px]" />
          <StageBadge stage={dossier.stage} className="text-[10px]" />
        </div>
      </button>
    </li>
  );
}

function DefaultLists({
  recents,
  all,
  onPick,
}: {
  recents: Dossier[];
  all: Dossier[];
  onPick: (id: string) => void;
}) {
  if (recents.length === 0 && all.length === 0) {
    return (
      <p className="text-sm italic text-neutral-400 px-3 py-6 text-center">
        Tapez pour rechercher un dossier.
      </p>
    );
  }
  return (
    <>
      {recents.length > 0 && (
        <>
          <SectionLabel>Récents</SectionLabel>
          <ul role="listbox">
            {recents.map((d) => (
              <DossierRow
                key={d.id}
                dossier={d}
                icon={<FolderOpen className="w-4 h-4 text-cime-primary" />}
                onPick={onPick}
              />
            ))}
          </ul>
        </>
      )}
      {all.length > 0 && (
        <>
          <SectionLabel>Tous les dossiers</SectionLabel>
          <ul role="listbox">
            {all.map((d) => (
              <DossierRow
                key={d.id}
                dossier={d}
                icon={<FolderOpen className="w-4 h-4 text-neutral-400" />}
                onPick={onPick}
              />
            ))}
          </ul>
        </>
      )}
    </>
  );
}

function SearchResults({
  results,
  isFetching,
  onPick,
}: {
  results: Dossier[];
  isFetching: boolean;
  onPick: (id: string) => void;
}) {
  if (isFetching && results.length === 0) {
    return <p className="text-sm text-neutral-400 px-3 py-6 text-center">Recherche…</p>;
  }
  if (results.length === 0) {
    return (
      <p className="text-sm italic text-neutral-400 px-3 py-6 text-center">Aucun résultat.</p>
    );
  }
  return (
    <ul role="listbox">
      {results.map((d) => (
        <DossierRow
          key={d.id}
          dossier={d}
          icon={<FolderOpen className="w-4 h-4 text-cime-primary" />}
          onPick={onPick}
        />
      ))}
    </ul>
  );
}
